/**
 * API Service for VSKR Legal Platform
 * Handles all HTTP requests with authentication
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token from localStorage
const getToken = () => localStorage.getItem('vskr_token');

// Create headers with optional auth
const createHeaders = (includeAuth = true, customHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Handle API response
const handleResponse = async (response) => {
  if (response.status === 401) {
    // Token expired or invalid - clear auth state
    localStorage.removeItem('vskr_token');
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'An error occurred');
  }

  return data;
};

// API methods
export const api = {
  // GET request
  get: async (endpoint, includeAuth = true) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: createHeaders(includeAuth),
    });
    return handleResponse(response);
  },

  // POST request
  post: async (endpoint, body, includeAuth = true) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: createHeaders(includeAuth),
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  // PUT request
  put: async (endpoint, body, includeAuth = true) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: createHeaders(includeAuth),
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  // PATCH request
  patch: async (endpoint, body, includeAuth = true) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: createHeaders(includeAuth),
      body: JSON.stringify(body),
    });
    return handleResponse(response);
  },

  // DELETE request
  delete: async (endpoint, includeAuth = true) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: createHeaders(includeAuth),
    });
    return handleResponse(response);
  },
};

// Auth-specific API calls
export const authApi = {
  login: (email, password) => api.post('/api/auth/login', { email, password }, false),
  register: (userData) => api.post('/api/auth/register', userData, false),
  getMe: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout', {}),
};

// Admin Audit / Visitor Intelligence / Sessions / Security
export const auditApi = {
  getLogs: (qs = '') => api.get(`/api/admin/audit/logs${qs ? `?${qs}` : ''}`),
  getSecurity: (qs = '') => api.get(`/api/admin/audit/security${qs ? `?${qs}` : ''}`),
  getVisitors: (qs = '') => api.get(`/api/admin/audit/visitors${qs ? `?${qs}` : ''}`),
  getVisitor: (id) => api.get(`/api/admin/audit/visitors/${id}`),
  getSessions: (qs = '') => api.get(`/api/admin/audit/sessions${qs ? `?${qs}` : ''}`),
  getSession: (id) => api.get(`/api/admin/audit/sessions/${id}`),
  getUserActivity: () => api.get('/api/admin/audit/user-activity'),
  getUserActivityDetail: (id) => api.get(`/api/admin/audit/user-activity/${id}`),
  getSecurityDashboard: () => api.get('/api/admin/audit/security-dashboard'),
};

// Cases API
export const casesApi = {
  getAll: () => api.get('/api/cases'),
  getById: (id) => api.get(`/api/cases/${id}`),
  create: (caseData) => api.post('/api/cases', caseData),
  update: (id, caseData) => api.put(`/api/cases/${id}`, caseData),
  delete: (id) => api.delete(`/api/cases/${id}`),
};

// Clients API
export const clientsApi = {
  getAll: () => api.get('/api/clients'),
  getById: (id) => api.get(`/api/clients/${id}`),
  create: (clientData) => api.post('/api/clients', clientData),
  update: (id, clientData) => api.put(`/api/clients/${id}`, clientData),
  delete: (id) => api.delete(`/api/clients/${id}`),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/api/admin/stats'),
  getAllUsers: (queryString = '') => api.get(`/api/admin/users${queryString ? `?${queryString}` : ''}`),
  getUserById: (id) => api.get(`/api/admin/users/${id}`),
  createUser: (userData) => api.post('/api/admin/users', userData),
  updateUser: (id, userData) => api.put(`/api/admin/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getPages: () => api.get('/api/admin/pages'),
  getUserPermissions: (id) => api.get(`/api/admin/users/${id}/permissions`),
  updatePermissions: (userId, permissions) => api.put(`/api/admin/users/${userId}/permissions`, permissions),
};

export default api;
